import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { passwordSchema } from '@shared/schemas/auth';
import { resetPassword } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const resetPasswordFormSchema = z.object({
  confirmPassword: passwordSchema,
  password: passwordSchema,
}).refine((values) => values.password === values.confirmPassword, {
  error: 'Les mots de passe doivent être identiques.',
  path: ['confirmPassword'],
});

type ResetPasswordFormValues = z.infer<typeof resetPasswordFormSchema>;

type ResetPasswordFormProps = React.ComponentProps<'form'> & {
  token: string | null;
};

export function ResetPasswordForm({
  className,
  token,
  ...props
}: ResetPasswordFormProps) {
  const navigate = useNavigate();
  const hasToken = useMemo(() => Boolean(token), [token]);
  const form = useForm<ResetPasswordFormValues>({
    defaultValues: {
      confirmPassword: '',
      password: '',
    },
    resolver: zodResolver(resetPasswordFormSchema),
  });

  async function handleSubmit(values: ResetPasswordFormValues) {
    form.clearErrors('root');

    if (!token) {
      form.setError('root', { message: 'Le lien de réinitialisation est invalide.' });
      return;
    }

    const result = await resetPassword(values.password, token);

    if (result.error) {
      const message = getAuthErrorMessage(result.error);
      form.setError('root', { message });
      toast.error(message);
      return;
    }

    toast.success('Mot de passe mis à jour. Tu peux te reconnecter.');
    navigate('/auth/login?reset=success', { replace: true });
  }

  return (
    <form
      data-testid="reset-password-form"
      className={cn('flex flex-col gap-6', className)}
      onSubmit={form.handleSubmit(handleSubmit)}
      {...props}
    >
      <FieldGroup>
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-bold">Choisis un nouveau mot de passe</h1>
          <p className="text-sm text-balance text-muted-foreground">
            {hasToken
              ? 'Le lien est valide. Définis maintenant ton nouveau mot de passe.'
              : 'Le token de réinitialisation est manquant ou invalide.'}
          </p>
        </div>

        <Field>
          <FieldLabel htmlFor="reset-password">Nouveau mot de passe</FieldLabel>
          <Input
            id="reset-password"
            data-testid="reset-password-input"
            type="password"
            autoComplete="new-password"
            className="bg-background"
            aria-invalid={form.formState.errors.password ? 'true' : 'false'}
            {...form.register('password')}
          />
          <FieldDescription>Au moins 8 caractères.</FieldDescription>
          <FieldError data-testid="reset-password-field-error" errors={[form.formState.errors.password]} />
        </Field>

        <Field>
          <FieldLabel htmlFor="reset-confirm-password">Confirmation</FieldLabel>
          <Input
            id="reset-confirm-password"
            data-testid="reset-password-confirm-input"
            type="password"
            autoComplete="new-password"
            className="bg-background"
            aria-invalid={form.formState.errors.confirmPassword ? 'true' : 'false'}
            {...form.register('confirmPassword')}
          />
          <FieldError errors={[form.formState.errors.confirmPassword]} />
        </Field>

        <FieldError data-testid="reset-password-root-error" errors={[form.formState.errors.root]} />

        <Field>
          <Button data-testid="reset-password-submit" type="submit" disabled={!hasToken || form.formState.isSubmitting}>
            {form.formState.isSubmitting ? <Loader2Icon className="animate-spin" /> : null}
            Enregistrer le nouveau mot de passe
          </Button>
        </Field>
      </FieldGroup>
    </form>
  );
}
